import apiClient from "../api";

export interface QuizQuestion {
  _id?: string;
  type:
    | "multiple_choice"
    | "true_false"
    | "short_answer"
    | "essay"
    | "fill_blank"
    | "matching";
  question: string;
  options?: string[];
  correctAnswer?: string;
  correctAnswers?: string[];
  points: number;
  explanation?: string;
  difficulty?: "easy" | "medium" | "hard";
  rubric?: Array<{
    criteria: string;
    points: number;
    description: string;
  }>;
  pairs?: Array<{
    left: string;
    right: string;
  }>;
  tags?: string[];
  estimatedTime?: number;
}

export interface Quiz {
  _id?: string;
  title: string;
  description?: string;
  instructions?: string;
  courseId: string;
  moduleId?: string;
  timeLimit: number;
  attempts: number;
  passingScore: number;
  questions: QuizQuestion[];
  shuffleQuestions: boolean;
  shuffleOptions: boolean;
  showResults: boolean;
  showCorrectAnswers: boolean;
  settings: {
    randomizeQuestions: boolean;
    oneQuestionAtATime: boolean;
    preventBacktracking: boolean;
    lockQuestionsAfterAnswering: boolean;
    requireWebcam: boolean;
    fullScreenMode: boolean;
    showProgressBar: boolean;
  };
  grading: {
    autoGrade: boolean;
    gradingMethod: "highest" | "latest" | "average";
    releaseGrades: "immediately" | "after_due_date" | "manual";
  };
  schedule: {
    availableFrom?: Date;
    availableUntil?: Date;
    dueDate?: Date;
  };
  isPublished: boolean;
  branchId: string;
  createdBy: string;
  course: {
    _id: string;
    name?: string;
    title?: string;
  };
  createdAt: string;
  updatedAt: string;
  totalPoints?: number;
  questionCount?: number;
  estimatedTime?: number;
  availabilityStatus?: string;
}

export interface QuizFormData {
  title: string;
  description?: string;
  instructions?: string;
  courseId: string;
  moduleId?: string;
  timeLimit: number;
  attempts: number;
  passingScore: number;
  shuffleQuestions: boolean;
  shuffleOptions: boolean;
  showResults: boolean;
  showCorrectAnswers: boolean;
  settings: {
    randomizeQuestions: boolean;
    oneQuestionAtATime: boolean;
    preventBacktracking: boolean;
    lockQuestionsAfterAnswering: boolean;
    requireWebcam: boolean;
    fullScreenMode: boolean;
    showProgressBar: boolean;
  };
  grading: {
    autoGrade: boolean;
    gradingMethod: "highest" | "latest" | "average";
    releaseGrades: "immediately" | "after_due_date" | "manual";
  };
  schedule: {
    availableFrom?: string;
    availableUntil?: string;
    dueDate?: string;
  };
}

export interface QuizListResponse {
  success: boolean;
  message: string;
  quizzes: Quiz[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface QuizResponse {
  success: boolean;
  message: string;
  data: Quiz;
}

export interface QuizAnalytics {
  quiz: {
    _id: string;
    title: string;
    totalPoints: number;
    questionCount: number;
    passingScore: number;
  };
  analytics: {
    totalAttempts: number;
    uniqueStudents: number;
    completedAttempts: number;
    averageScore: number;
    averageTimeSpent: number;
    passRate: number;
    scoreDistribution: {
      "0-20": number;
      "21-40": number;
      "41-60": number;
      "61-80": number;
      "81-100": number;
    };
    recentAttempts: Array<{
      _id: string;
      studentId: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
      };
      score: number;
      status: string;
      timeSpent: number;
      createdAt: string;
    }>;
  };
}

export class QuizService {
  private baseUrl = "/elearning/quizzes";

  // Create a new quiz
  async createQuiz(quizData: QuizFormData): Promise<QuizResponse> {
    try {
      const response = await apiClient.post<QuizResponse>(
        this.baseUrl,
        quizData
      );
      return response as unknown as QuizResponse;
    } catch (error: any) {
      // Extract specific error message from API response
      const errorData = error.response?.data;
      if (errorData?.message) {
        // If there are validation errors array, format them nicely
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const validationMessages = errorData.errors.map((err: any) => {
            if (err.questionIndex) {
              return `Question ${err.questionIndex}: ${
                err.errors?.join(", ") || err.message
              }`;
            }
            return err.message || err;
          });
          throw new Error(
            `${errorData.message}\n${validationMessages.join("\n")}`
          );
        }
        // Return the specific backend error message
        throw new Error(errorData.message);
      }
      // Fallback to generic error
      throw error;
    }
  }

  // Get all quizzes for the current teacher
  async getQuizzes(filters?: {
    courseId?: string;
    status?: "published" | "draft";
    page?: number;
    limit?: number;
  }): Promise<QuizListResponse> {
    const params = new URLSearchParams();

    if (filters?.courseId) params.append("courseId", filters.courseId);
    if (filters?.status) params.append("status", filters.status);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());

    const queryString = params.toString();
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;

    const response = await apiClient.get<QuizListResponse>(url);
    return response as unknown as QuizListResponse;
  }

  // Get quiz by ID
  async getQuizById(id: string): Promise<QuizResponse> {
    const response = await apiClient.get<QuizResponse>(`${this.baseUrl}/${id}`);
    return response as unknown as QuizResponse;
  }

  // Update quiz
  async updateQuiz(
    id: string,
    quizData: Partial<QuizFormData>
  ): Promise<QuizResponse> {
    const response = await apiClient.put<QuizResponse>(
      `${this.baseUrl}/${id}`,
      quizData
    );
    return response as unknown as QuizResponse;
  }

  // Delete quiz
  async deleteQuiz(id: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.delete<{
      success: boolean;
      message: string;
    }>(`${this.baseUrl}/${id}`);
    return response as unknown as { success: boolean; message: string };
  }

  // Publish/unpublish quiz
  async toggleQuizPublication(
    id: string,
    isPublished: boolean
  ): Promise<{
    success: boolean;
    message: string;
    data: { isPublished: boolean };
  }> {
    const response = await apiClient.patch<{
      success: boolean;
      message: string;
      data: { isPublished: boolean };
    }>(`${this.baseUrl}/${id}/publish`, { isPublished });
    return response as unknown as {
      success: boolean;
      message: string;
      data: { isPublished: boolean };
    };
  }

  // Add question to quiz
  async addQuestion(
    quizId: string,
    question: QuizQuestion
  ): Promise<QuizResponse> {
    const response = await apiClient.post<QuizResponse>(
      `${this.baseUrl}/${quizId}/questions`,
      question
    );
    return response as unknown as QuizResponse;
  }

  // Update question in quiz
  async updateQuestion(
    quizId: string,
    questionId: string,
    question: Partial<QuizQuestion>
  ): Promise<QuizResponse> {
    const response = await apiClient.put<QuizResponse>(
      `${this.baseUrl}/${quizId}/questions/${questionId}`,
      question
    );
    return response as unknown as QuizResponse;
  }

  // Remove question from quiz
  async removeQuestion(
    quizId: string,
    questionId: string
  ): Promise<QuizResponse> {
    const response = await apiClient.delete<QuizResponse>(
      `${this.baseUrl}/${quizId}/questions/${questionId}`
    );
    return response as unknown as QuizResponse;
  }

  // Get quiz analytics
  async getQuizAnalytics(
    id: string
  ): Promise<{ success: boolean; message: string; data: QuizAnalytics }> {
    const response = await apiClient.get<{
      success: boolean;
      message: string;
      data: QuizAnalytics;
    }>(`${this.baseUrl}/${id}/analytics`);
    return response as unknown as {
      success: boolean;
      message: string;
      data: QuizAnalytics;
    };
  }

  // Helper method to get default quiz form data
  getDefaultQuizFormData(): QuizFormData {
    return {
      title: "",
      description: "",
      instructions: "Read each question carefully and select the best answer.",
      courseId: "",
      moduleId: "",
      timeLimit: 0,
      attempts: 1,
      passingScore: 60,
      shuffleQuestions: false,
      shuffleOptions: false,
      showResults: true,
      showCorrectAnswers: true,
      settings: {
        randomizeQuestions: false,
        oneQuestionAtATime: false,
        preventBacktracking: false,
        lockQuestionsAfterAnswering: false,
        requireWebcam: false,
        fullScreenMode: false,
        showProgressBar: true,
      },
      grading: {
        autoGrade: true,
        gradingMethod: "highest",
        releaseGrades: "immediately",
      },
      schedule: {
        availableFrom: undefined,
        availableUntil: undefined,
        dueDate: undefined,
      },
    };
  }

  // Helper method to get default question data
  getDefaultQuestionData(type: QuizQuestion["type"]): QuizQuestion {
    const baseQuestion: QuizQuestion = {
      type,
      question: "",
      points: 1,
      explanation: "",
      difficulty: "medium",
      tags: [],
      estimatedTime: 60,
    };

    switch (type) {
      case "multiple_choice":
        return {
          ...baseQuestion,
          options: ["", "", "", ""],
          correctAnswer: "",
        };
      case "true_false":
        return {
          ...baseQuestion,
          options: ["True", "False"],
          correctAnswer: "",
        };
      case "short_answer":
      case "essay":
        return {
          ...baseQuestion,
          correctAnswer: "",
        };
      case "fill_blank":
        return {
          ...baseQuestion,
          correctAnswers: [],
        };
      case "matching":
        return {
          ...baseQuestion,
          pairs: [
            { left: "", right: "" },
            { left: "", right: "" },
          ],
        };
      default:
        return baseQuestion;
    }
  }
}

export const quizService = new QuizService();
